import { Component, computed, effect, ElementRef, inject, input, OnDestroy, signal, viewChild } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, tap } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { commaSeparated, JalaliDateHelper, shortNumber } from '@tools';
import { SymbolDetailSectionId } from '../../models/symbol-detail-section.model';
import { SymbolDetailLayoutStore } from './data-access/symbol-detail-layout.store';
import { MarketDataStore, resolveLastPrice } from '../../core/market-data';
import { KalaSymbolCategory } from '../../models/symbol-info.model';
import { MarketDepthBestLimitComponent } from '../market-depth-best-limit/market-depth-best-limit.component';
import {
	mapMarketViewMessageToDepthValues,
	mapSymbolOrderBookToDepthValues,
	mapToSessionClosingPrice
} from '../market-depth-best-limit/models/market-depth.model';
import { MarketViewService } from '../market/services/market-view.service';
import { OrdersComponent } from '../orders/orders.component';
import { BuySellButtonComponent } from './components/buy-sell-button/buy-sell-button.component';
import { SymbolDetailCandleCardComponent } from './components/symbol-detail-candle-card/symbol-detail-candle-card.component';
import { SymbolDetailHeaderComponent } from './components/symbol-detail-header/symbol-detail-header.component';
import { SymbolInfoComponent, SymbolInfoViewModel } from './components/symbol-info/symbol-info.component';
import { SymbolDetailHeaderEventConsumerService } from './events/symbol-detail-header-event-consumer.service';
import { SymbolDetailHeaderEventProducerService } from './events/symbol-detail-header-event-producer.service';
import { SymbolDetailSectionsBottomSheetComponent } from './components/symbol-detail-sections-bottom-sheet/symbol-detail-sections-bottom-sheet.component';
import { SymbolTrendChartComponent } from './components/symbol-trend-chart/symbol-trend-chart.component';
import {
	SymbolDetailTabId,
	SymbolDetailTabsComponent
} from './components/symbol-detail-tabs/symbol-detail-tabs.component';
import { SymbolMessagesComponent } from './components/symbol-messages/symbol-messages.component';
import { TradeHistoryComponent } from './components/trade-history/trade-history.component';
import { SymbolDetailMoreBottomSheetComponent } from './components/symbol-detail-more-bottom-sheet/symbol-detail-more-bottom-sheet.component';
import { IconNames } from '@icon/assets';
import { symbolIcon } from '../../models/symbol.model';

/**
 * Comma-separated whole figure, or "-" when there is nothing to show. A symbol that has
 * not traded reports no value and no volume, and «-» says that where «۰» would claim a real
 * zero — the same reading `lastCalculatedTradeTime` already gives the same state.
 *
 * Every figure here is a rial amount or a share count, so it is rounded rather than left to
 * `toLocaleString`'s three decimals: a closing price reads «۱٬۵۰۰٬۴۱۹», never «۱٬۵۰۰٬۴۱۸٫۹».
 * The guard still reads the raw value, so a figure that rounds to zero is not mistaken for
 * one that never arrived.
 */
const formatFigure = (value: number | null | undefined): string => (value ? commaSeparated(Math.round(value)) : '-');

const formatTradeValue = (value: number | null | undefined): string => {
	if (!value) {
		return '-';
	}
	const [figure, unit] = shortNumber(value * 1000, 'KMB').split(' ');

	return unit ? `${commaSeparated(figure)} ${unit}` : figure;
};

export interface SymbolDetailType {
	name: string;
	subtitle: string;
	lastPrice: number;
	lastPriceChange: number;
	lastPriceChangePercent: number;
	closingPrice: number;
	closingPriceChangePercent: number;
	volume: number;
	icon: IconNames | null;
}

@Component({
	selector: 'app-symbol-detail',
	templateUrl: './symbol-detail.component.html',
	imports: [
		SymbolDetailHeaderComponent,
		SymbolInfoComponent,
		SymbolTrendChartComponent,
		MarketDepthBestLimitComponent,
		SymbolDetailCandleCardComponent,
		SymbolDetailTabsComponent,
		SymbolMessagesComponent,
		TradeHistoryComponent,
		OrdersComponent,
		BuySellButtonComponent,
		SymbolDetailSectionsBottomSheetComponent,
		SymbolDetailMoreBottomSheetComponent,
		UiButtonComponent,
		UIIconComponent
	],
	providers: [SymbolDetailHeaderEventProducerService, SymbolDetailHeaderEventConsumerService, MarketViewService],
	host: {
		class: 'block h-full'
	}
})
export class SymbolDetailComponent implements OnDestroy {
	private readonly symbolDetailHeaderProducer = inject(SymbolDetailHeaderEventProducerService);
	private readonly symbolDetailHeaderConsumer = inject(SymbolDetailHeaderEventConsumerService);
	private readonly marketData = inject(MarketDataStore);
	private readonly marketViewService = inject(MarketViewService);
	private readonly symbolDetailLayoutStore = inject(SymbolDetailLayoutStore);

	readonly symbolIsin = input.required<string>();

	/**
	 * «جزئیات», «سفارش‌ها», «سابقه معاملات» and «پیام‌ها» have panels. «بیشتر» never lands here
	 * at all — it raises the more sheet and leaves the current panel standing.
	 */
	readonly activeTab = signal<SymbolDetailTabId>('details');

	readonly headerCollapsed = signal(false);

	private readonly headerElement = viewChild(SymbolDetailHeaderComponent, { read: ElementRef<HTMLElement> });

	private readonly tabsElement = viewChild(SymbolDetailTabsComponent, { read: ElementRef<HTMLElement> });

	private readonly ordersComponent = viewChild(OrdersComponent);

	readonly ordersToolbarActive = computed(() => this.ordersComponent()?.toolbarActive() ?? false);

	private previousScrollTop = 0;

	readonly sectionOrder = this.symbolDetailLayoutStore.sectionOrder;

	readonly expanded = this.symbolDetailLayoutStore.expanded;

	private readonly symbolResource = rxResource({
		params: () => this.symbolIsin(),
		stream: ({ params: isin }) => this.marketData.getSymbol(isin)
	});

	readonly symbolMeta = computed(() => (this.symbolResource.hasValue() ? this.symbolResource.value() : undefined));

	/** Sourced from the REST symbol snapshot — real isins carry no parseable contractId of their own. */
	private readonly contractId = computed(() => this.symbolMeta()?.contractId);

	readonly liveMarketView = toSignal(
		toObservable(this.contractId).pipe(
			switchMap(contractId =>
				contractId === undefined ? of(undefined) : this.marketViewService.connectSingleContract(contractId)
			)
		),
		{ initialValue: undefined }
	);

	private readonly lastPrice = computed(() => resolveLastPrice(this.liveMarketView(), this.symbolMeta()));

	readonly previousClosePrice = computed(() => this.lastPrice().previousClose);

	readonly closingPrice = computed(() => mapToSessionClosingPrice(this.liveMarketView(), this.symbolMeta()));

	readonly depthValues = computed(() => {
		const live = this.liveMarketView();
		return live ? mapMarketViewMessageToDepthValues(live) : mapSymbolOrderBookToDepthValues(this.symbolMeta());
	});

	readonly symbol = computed<SymbolDetailType>(() => {
		const meta = this.symbolMeta();
		const live = this.liveMarketView();

		const { lastPrice, lastPriceChange, previousClose } = this.lastPrice();
		const closingPriceChangePercent = previousClose ? (this.closingPrice() - previousClose) / previousClose : 0;

		return {
			// `name` matches OrderFormHeaderSymbol's convention (short name); `subtitle` carries
			// the fuller descriptive name shown only on the symbol-detail header.
			name: meta?.symbolShortName ?? '',
			subtitle: meta?.symbolName ?? '',
			lastPrice,
			lastPriceChange,
			lastPriceChangePercent: previousClose ? (lastPriceChange / previousClose) * 100 : 0,
			closingPrice: this.closingPrice(),
			closingPriceChangePercent: closingPriceChangePercent * 100,
			volume: live?.tv ?? meta?.tradesVolume ?? 0,
			icon: meta?.symbol ? symbolIcon(meta.isin) : null
		};
	});

	readonly symbolInfoData = computed<Partial<SymbolInfoViewModel>>(() => {
		const meta = this.symbolMeta();
		const live = this.liveMarketView();

		const previousClose = this.previousClosePrice();
		const totalTradeValue = live?.tval ?? meta?.tradesValue;
		const totalNumberOfSharesTraded = live?.tv ?? meta?.tradesVolume;
		// `||` covers every no-data shape: an unparseable time makes getFormattedJDateTime return
		// null, and the snapshot's field arrives as null when the symbol hasn't traded.
		const lastTradeTime = live?.ltpt || meta?.lastTradedPriceTime;
		const lastCalculatedTradeTime =
			(lastTradeTime ? JalaliDateHelper.getFormattedJDateTime(new Date(lastTradeTime)) : null) || '-';
		const closingPriceChangePercent = previousClose ? (this.closingPrice() - previousClose) / previousClose : 0;
		const closingPriceChange = this.closingPrice() ? this.closingPrice() - previousClose : 0;

		return {
			symbolCategory: KalaSymbolCategory.Govahi,
			lastTradedPrice: this.lastPrice().lastPrice,
			closingPrice: formatFigure(this.closingPrice()),
			closingPriceChangePercent: closingPriceChangePercent * 100,
			feeOfPreviousDaysClosingPrice: formatFigure(previousClose),
			totalTradeValue: formatTradeValue(totalTradeValue),
			totalNumberOfSharesTraded: formatFigure(totalNumberOfSharesTraded),
			lastCalculatedTradeTime,
			tradesCount: formatFigure(live?.tc ?? meta?.tradesCount),
			closingPriceChange
		};
	});

	constructor() {
		this.symbolDetailHeaderProducer.configureSymbolDetailHeader();

		effect(() => {
			const meta = this.symbolMeta();
			if (!meta) {
				return;
			}

			this.symbolDetailHeaderProducer.configureSymbolDetailHeader({
				name: meta.symbolShortName,
				groupId: meta.marketGroupId
			});
		});

		toSignal(
			this.symbolDetailHeaderConsumer.headerAction$.pipe(
				tap(event => {
					if (event?.data?.eventId === 'back') {
						this.goBack();
					}
					this.symbolDetailHeaderProducer.clearEventSnapshot(event.id);
				})
			)
		);
	}

	ngOnDestroy(): void {
		this.symbolDetailHeaderProducer.resetHeader();
	}

	onBodyScroll(body: HTMLElement): void {
		const { scrollTop, scrollHeight, clientHeight } = body;
		const hideThreshold =
			(this.headerElement()?.nativeElement.offsetHeight ?? 0) +
			(this.tabsElement()?.nativeElement.offsetHeight ?? 0) +
			10;
		const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
		if (scrollTop > this.previousScrollTop) {
			if (scrollTop > hideThreshold) this.headerCollapsed.set(true);
		} else if (!atBottom || scrollTop <= hideThreshold) {
			this.headerCollapsed.set(false);
		}
		this.previousScrollTop = scrollTop;
	}

	onSectionOrderChanged(sectionOrder: SymbolDetailSectionId[]): void {
		this.symbolDetailLayoutStore.saveSectionOrder(sectionOrder);
	}

	onLayoutReset(): void {
		this.symbolDetailLayoutStore.resetToDefault();
	}

	/** Back steps out of a secondary tab to «جزئیات» first; only from «جزئیات» does it leave the page. */
	private goBack(): void {
		if (this.activeTab() !== 'details') {
			this.activeTab.set('details');
			return;
		}
		history.go(-1);
	}
}
